import React, { useEffect, useRef, useState } from 'react'
import * as S from './styles'
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import { IconButton, Skeleton } from '@mui/material';
import axios from 'axios';
import { ChevronLeft } from '@material-ui/icons';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import NearbyErrorIcon from '@mui/icons-material/NearbyError';
import SendIcon from '@mui/icons-material/Send';
import { useTypingEffect } from '../../../../../hooks/useTypingEffect';
import TextField from "@material-ui/core/TextField";
import { createMuiTheme, ThemeProvider } from "@material-ui/core/styles";
import CircularProgress from '@mui/material/CircularProgress';

const theme = createMuiTheme({
    overrides: { MuiInputBase: { root:{
        fontFamily: "Open Sans",
        fontSize: "14px",
        lineHeight: "19px"

    }}},
    palette:{
        primary: {
            main:"#0b1f82"
        },
    }
  });

export default function AiInsightsCard({ alert_key, setAutoFilledContent, canAutoFill }) {

    const [aiInsights, setAiInsights] = useState({ insights: [] });
    const [lastItemIsFromPost, setLastItemIsFromPost] = useState(false);
    const [error, setError] = useState(false);
    const [userPromptError, setUserPromptError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isUserPromptLoading, setIsUserPromptLoading] = useState(false);
    const [hasAutofilledContent, setHasAutofilledContent] = useState(false);
    const [expanded, setExpanded] = useState(true);
    const contentRef = useRef(null);
    const [contentHeight, setContentHeight] = useState(0);

    const [userPromptText, setUserPromptText] = useState('');
    const insightsList = Array.isArray(aiInsights?.insights) ? aiInsights.insights : [];
    const assistants = insightsList.filter((i) => i?.role === 'assistant');
    const lastAssistant = assistants.length > 0 ? assistants[assistants.length - 1] : null;
    const lastContent = lastAssistant?.content;
    const lastReasoningText = lastContent?.reasoning ?? '';

    const lastFinalResponse = (() => {
        for (let i = insightsList.length - 1; i >= 0; i--) {
            const content = insightsList[i]?.role === 'assistant' && insightsList[i]?.content?.final_response;
            if (content) return insightsList[i].content.final_response;
        }
        return '';
    })();
    const lastRecommendationInList = (() => {
        for (let i = insightsList.length - 1; i >= 0; i--) {
            const content = insightsList[i]?.role === 'assistant' && insightsList[i]?.content?.recommendation;
            if (content) return insightsList[i].content.recommendation;
        }
        return null;
    })();

    const [typedReasoning, reasoningFinished] = useTypingEffect(
        lastReasoningText,
        5,
        lastItemIsFromPost && !isLoading && !!lastReasoningText
    );

    const lastAssistantFinalResponse = lastContent?.final_response ?? '';
    const [typedFinal, finalFinished] = useTypingEffect(
        lastAssistantFinalResponse,
        5,
        lastItemIsFromPost && !isLoading && reasoningFinished && !!lastAssistantFinalResponse
    );

    const submitUserPrompt = () => {
        if (isUserPromptLoading || !userPromptText.trim()) return;

        setIsUserPromptLoading(true);
        setUserPromptError(false);
        axios.post(`/dash/alerts/alert/${alert_key}/ai_insights/reply`, {
            message: userPromptText,
        })
        .then(response => {
            setAiInsights(prev => ({
                insights: [
                    ...(prev?.insights ?? []),
                    { role: 'user', content: { message: userPromptText } },
                    { role: 'assistant', content: response.data },
                ],
            }));
            setLastItemIsFromPost(true);
            setUserPromptText('');
        })
        .catch(() => {
            setUserPromptError(true);
        })
        .finally(() => {
            setIsUserPromptLoading(false);
        });
    }

    const SkeletonParagraph = (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ display: "flex", gap: 6 }}>
                    <Skeleton variant="text" width="23%" height={12} />
                    <Skeleton variant="text" width="11%" height={12} />
                    <Skeleton variant="text" width="9%" height={12} />
                    <Skeleton variant="text" width="14%" height={12} />
                    <Skeleton variant="text" width="7%" height={12} />
                    <Skeleton variant="text" width="19%" height={12} />
                    <Skeleton variant="text" width="8%" height={12} />
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                    <Skeleton variant="text" width="12%" height={12} />
                    <Skeleton variant="text" width="7%" height={12} />
                    <Skeleton variant="text" width="17%" height={12} />
                    <Skeleton variant="text" width="10%" height={12} />
                    <Skeleton variant="text" width="15%" height={12} />
                    <Skeleton variant="text" width="9%" height={12} />
                    <Skeleton variant="text" width="18%" height={12} />
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                    <Skeleton variant="text" width="14%" height={12} />
                    <Skeleton variant="text" width="9%" height={12} />
                    <Skeleton variant="text" width="21%" height={12} />
                    <Skeleton variant="text" width="6%" height={12} />
                    <Skeleton variant="text" width="17%" height={12} />
                    <Skeleton variant="text" width="8%" height={12} />
                    <Skeleton variant="text" width="13%" height={12} />
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                    <Skeleton variant="text" width="19%" height={12} />
                    <Skeleton variant="text" width="8%" height={12} />
                    <Skeleton variant="text" width="11%" height={12} />
                    <Skeleton variant="text" width="16%" height={12} />
                    <Skeleton variant="text" width="7%" height={12} />
                    <Skeleton variant="text" width="14%" height={12} />
                    <Skeleton variant="text" width="10%" height={12} />
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                    <Skeleton variant="text" width="15%" height={12} />
                    <Skeleton variant="text" width="11%" height={12} />
                    <Skeleton variant="text" width="8%" height={12} />
                    <Skeleton variant="text" width="18%" height={12} />
                    <Skeleton variant="text" width="10%" height={12} />
                    <Skeleton variant="text" width="13%" height={12} />
                    <Skeleton variant="text" width="9%" height={12} />
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                    <Skeleton variant="text" width="17%" height={12} />
                    <Skeleton variant="text" width="9%" height={12} />
                    <Skeleton variant="text" width="14%" height={12} />
                    <Skeleton variant="text" width="7%" height={12} />
                    <Skeleton variant="text" width="19%" height={12} />
                    <Skeleton variant="text" width="8%" height={12} />
                    <Skeleton variant="text" width="12%" height={12} />
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                    <Skeleton variant="text" width="13%" height={12} />
                    <Skeleton variant="text" width="7%" height={12} />
                    <Skeleton variant="text" width="16%" height={12} />
                    <Skeleton variant="text" width="10%" height={12} />
                    <Skeleton variant="text" width="21%" height={12} />
                    <Skeleton variant="text" width="9%" height={12} />
                    <Skeleton variant="text" width="11%" height={12} />
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                    <Skeleton variant="text" width="18%" height={12} />
                    <Skeleton variant="text" width="11%" height={12} />
                    <Skeleton variant="text" width="9%" height={12} />
                    <Skeleton variant="text" width="14%" height={12} />
                    <Skeleton variant="text" width="7%" height={12} />
                    <Skeleton variant="text" width="20%" height={12} />
                    <Skeleton variant="text" width="8%" height={12} />
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                    <Skeleton variant="text" width="16%" height={12} />
                    <Skeleton variant="text" width="8%" height={12} />
                    <Skeleton variant="text" width="19%" height={12} />
                    <Skeleton variant="text" width="6%" height={12} />
                    <Skeleton variant="text" width="13%" height={12} />
                    <Skeleton variant="text" width="9%" height={12} />
                    <Skeleton variant="text" width="15%" height={12} />
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                    <Skeleton variant="text" width="14%" height={12} />
                    <Skeleton variant="text" width="7%" height={12} />
                    <Skeleton variant="text" width="17%" height={12} />
                    <Skeleton variant="text" width="11%" height={12} />
                    <Skeleton variant="text" width="9%" height={12} />
                    <Skeleton variant="text" width="19%" height={12} />
                    <Skeleton variant="text" width="10%" height={12} />
                </div>
            </div>
    );

    let contentElement;
    if (error) {
        contentElement = (
            <div>
                <S.ErrorText>Não foi possível carregar os insights. Por favor, tente novamente mais tarde.</S.ErrorText>
            </div>
        );
    } else if (isLoading && insightsList.length === 0) {
        contentElement = SkeletonParagraph;
    } else {
        const lastAssistantIdx = (() => {
            for (let i = insightsList.length - 1; i >= 0; i--) {
                if (insightsList[i]?.role === 'assistant') return i;
            }
            return -1;
        })();
        const settledInsights = lastItemIsFromPost && lastAssistantIdx >= 0
            ? insightsList.slice(0, lastAssistantIdx)
            : lastItemIsFromPost ? [] : insightsList;

        const lastReasoningDisplay = lastItemIsFromPost ? typedReasoning : lastReasoningText;
        const lastFinalDisplay = lastItemIsFromPost && lastAssistantFinalResponse
            ? typedFinal
            : lastFinalResponse;
        const showFinal = !!lastFinalDisplay;
        const lastRecommendation = lastRecommendationInList;

        contentElement = (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <S.TitleWrapper style={{ marginTop: '16px' }}>
                    <SettingsSuggestIcon style={{ fontSize: 20, color: '#0b1f82' }} />
                    <S.Title>Resposta</S.Title>
                </S.TitleWrapper>
                {settledInsights.map((insight, idx) => (
                    <S.SectionContainer key={idx}>
                        {insight?.role === 'user' ? (
                            <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                                <S.UserMessageWrapper>
                                    <S.Description style={{ margin: 0 }}>{insight.content?.message}</S.Description>
                                </S.UserMessageWrapper>
                            </div>
                        ) : (
                            <S.Description>{insight.content?.reasoning}</S.Description>
                        )}
                    </S.SectionContainer>
                ))}
                {lastAssistant && lastItemIsFromPost && (
                    <S.SectionContainer key="last-typing">
                        <S.Description>{lastReasoningDisplay}</S.Description>
                    </S.SectionContainer>
                )}
                <div style={{
                    overflow: 'hidden',
                    maxHeight: (reasoningFinished || !lastItemIsFromPost) ? 'fit-content' : 0,
                    transition: 'max-height 0.3s ease',
                }}>
                    <S.UserPromptWrapper>
                        <ThemeProvider theme={theme}>
                            <TextField
                                placeholder='Digite sua pergunta ou instrução...'
                                fullWidth
                                multiline
                                variant="outlined"
                                margin="dense"
                                rows="5"
                                color="primary"
                                value={userPromptText}
                                onChange={(e) => setUserPromptText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        submitUserPrompt();
                                    }
                                }}
                                disabled={isUserPromptLoading}
                            />
                        </ThemeProvider>
                        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 4, width: '100%', alignItems: 'center' }}>
                            {userPromptError ? (
                                <S.ErrorText style={{ margin: 0 }}>Erro ao enviar resposta. Por favor, tente novamente.</S.ErrorText>
                            ) : (
                                <div></div>
                            )}
                            <div 
                                className={["button", "standard", "normalText", "normalMediumSize"].join(" ")}
                                onClick={submitUserPrompt}
                                disabled={isUserPromptLoading}
                                style={{ width: 'fit-content', margin: "0", display: "flex", alignItems: "center", gap: 4, backgroundColor: isUserPromptLoading ? '#bababa' : '#0b1f82' }}
                            >
                                {isUserPromptLoading ? <CircularProgress size={16} color="inherit" /> : (
                                    <SendIcon style={{ fontSize: 16, color: '#fff' }} />
                                )}
                                Responder
                            </div>
                        </div>
                    </S.UserPromptWrapper>
                </div>
                {showFinal && lastFinalDisplay && (
                    <S.SectionContainer style={{ marginTop: '16px' }}>
                            <S.TitleWrapper>
                                <AutoFixHighIcon style={{ fontSize: 20, color: '#0b1f82' }} />
                                <S.Title>Comentário final</S.Title>
                            </S.TitleWrapper>
                            <S.Description>{lastFinalDisplay}</S.Description>
                        </S.SectionContainer>
                )}
                {(() => {
                    const waitForTyping = lastItemIsFromPost && !!lastAssistantFinalResponse;
                    const showConclusao = showFinal && (waitForTyping ? finalFinished : true) && lastRecommendation;
                    return showConclusao && (
                    <S.SectionContainer style={{ marginTop: "8px" }}>
                        <S.TitleWrapper>
                            <NearbyErrorIcon style={{ fontSize: 20, color: '#0b1f82' }} />
                            <S.Title>Conclusão:</S.Title>
                            <S.Badge
                                $recommendation={lastRecommendation}
                                style={{
                                    opacity: lastRecommendation ? 1 : 0,
                                    transform: lastRecommendation ? 'translateY(0)' : 'translateY(-6px)',
                                    transition: 'opacity 200ms ease, transform 200ms ease',
                                }}
                            >{lastRecommendation === 'falso positivo' ? lastRecommendation : 'Alerta válido: ' + lastRecommendation}</S.Badge>
                        </S.TitleWrapper>
                        {(waitForTyping ? finalFinished : true) && canAutoFill && (
                            <S.FooterWrapper>
                                <div
                                    className={["button", "standard", "normalText", "normalMediumSize"].join(" ")}
                                    onClick={() => {
                                        navigator.clipboard.writeText(lastFinalDisplay);
                                        setHasAutofilledContent(true);
                                        setTimeout(() => setHasAutofilledContent(false), 5000);
                                        setAutoFilledContent(lastFinalDisplay);
                                    }}
                                >
                                    {hasAutofilledContent ? "Comentário autopreenchido!" : "Autopreencher comentário"}
                                </div>
                            </S.FooterWrapper>
                        )}
                    </S.SectionContainer>
                    );
                })()}
            </div>
        );
    }

    useEffect(() => {
        if (!alert_key) return;
        const source = axios.CancelToken.source();
        setIsLoading(true);
        setError(false);
        setLastItemIsFromPost(false);

        axios.get(`/dash/alerts/alert/${alert_key}/ai_insights/session`, { cancelToken: source.token })
            .then(response => {
                setAiInsights(response.data ?? { insights: [] });
                setLastItemIsFromPost(false);
                setIsLoading(false);
            })
            .catch(err => {
                if (axios.isCancel(err)) return;
                if (err.response?.status === 404) {
                    axios.post(`/dash/alerts/alert/${alert_key}/ai_insights`, null, { cancelToken: source.token })
                        .then(response => {
                            setAiInsights({
                                insights: [{ role: 'assistant', content: response.data }],
                            });
                            setLastItemIsFromPost(true);
                            setIsLoading(false);
                        })
                        .catch(postErr => {
                            if (axios.isCancel(postErr)) return;
                            setError(true);
                            setIsLoading(false);
                        });
                } else {
                    setError(true);
                    setIsLoading(false);
                }
            });

        return () => {
            source.cancel('component unmounted');
        };
    }, [alert_key]);

    useEffect(() => {
        if (!contentRef.current) return;
        const id = requestAnimationFrame(() => {
            const nextHeight = contentRef.current?.scrollHeight || 0;
            setContentHeight(nextHeight);
        });
        return () => cancelAnimationFrame(id);
    }, [expanded, aiInsights, isLoading, typedReasoning, typedFinal, reasoningFinished, finalFinished, userPromptError]);

    return (
        <div className="analysisCard" style={{ transition: 'all 0.3s ease', height: 'fit-content', overflow: 'hidden' }}>
            <S.Header>
                <S.TitleWrapper>
                    <TipsAndUpdatesIcon style={{ fontSize: 20, color: 'orange' }} />
                    <S.Title>AI Insights</S.Title>
                </S.TitleWrapper>
                <S.ButtonWrapper $expanded={expanded}>
                    <IconButton onClick={() => setExpanded(!expanded)}>
                        <ChevronLeft />
                    </IconButton>
                </S.ButtonWrapper>
            </S.Header>
            <div
                style={{
                    overflow: 'hidden',
                    maxHeight: expanded ? contentHeight : 0,
                    minHeight: 0,
                    opacity: expanded ? 1 : 0,
                    transition: 'max-height 0.35s ease, opacity 0.25s ease',
                }}
            >
                <div ref={contentRef}>
                    <S.Content>
                        {contentElement}
                    </S.Content>
                </div>
            </div>
        </div>
    )
}